import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../constants/config';
import { createBaseQuery } from './baseQuery';

export interface CertificateInfo {
  name: string;
  serialNumber: string;
  expiryDate?: string;
  expiryStatus: 'Valid' | 'Warning' | 'Critical' | 'Expired';
  daysUntilExpiry: number;
  organizationIds: string[];
  clientIds: string[];
  totalUsageCount: number;
  lastUsed?: string;
  isSystemCertificate: boolean;
}

export interface CertificateStats {
  totalCertificates: number;
  expiredCertificates: number;
  criticalCertificates: number;
  warningSertificates: number;
  validCertificates: number;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetCertificatesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  expiryStatus?: string;
}

export const certificatesApi = createApi({
  reducerPath: 'certificatesApi',
  baseQuery: createBaseQuery(`${config.apiUrl}/api/certificates`),
  tagTypes: ['Certificate'],
  endpoints: (builder) => ({
    getCertificates: builder.query<PagedResult<CertificateInfo>, GetCertificatesParams>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
        if (params.search) searchParams.append('search', params.search);
        if (params.expiryStatus) searchParams.append('expiryStatus', params.expiryStatus);

        const queryString = searchParams.toString();
        return queryString ? `?${queryString}` : '';
      },
      providesTags: ['Certificate'],
    }),

    getCertificateStats: builder.query<CertificateStats, void>({
      query: () => '/stats',
      providesTags: ['Certificate'],
    }),
  }),
});

export const {
  useGetCertificatesQuery,
  useGetCertificateStatsQuery,
} = certificatesApi;